import React, { useState } from 'react';
import {
  AthleteSnapshot,
  SectionKey,
  auditProfileCompleteness,
} from '../hooks/useProfileCompleteness';

/**
 * SectionCompletenessPill
 *
 * Compact "N missing" pill rendered next to each section header on
 * PlayerProfile. Tap routes to the section's own edit modal. Sections
 * with no recommended-tier gaps render nothing — the page stays quiet
 * when there's nothing to do.
 *
 * Hovering the pill surfaces a branded tooltip listing the specific
 * fields that are missing. Replaces the native `title` attribute, which
 * showed the OS-default gray bubble with a half-second delay — felt
 * sluggish and stylistically off-brand.
 */

interface Props {
  athlete: AthleteSnapshot;
  section: SectionKey;
  onPress: () => void;
}

const SectionCompletenessPill: React.FC<Props> = ({ athlete, section, onPress }) => {
  const [hovered, setHovered] = useState(false);
  const audit = auditProfileCompleteness(athlete);
  const sectionAudit = audit.sections[section];
  const missing = sectionAudit?.missingRecommended.length ?? 0;
  if (missing === 0) return null;

  const label = missing === 1 ? '1 missing' : `${missing} missing`;
  const fields = sectionAudit.missingRecommended;

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        onClick={onPress}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        aria-label={`${missing} missing: ${fields.join(', ')}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '3px 10px 3px 8px',
          background: 'white',
          border: '1px solid rgba(249, 85, 13, 0.45)',
          borderRadius: 999,
          cursor: 'pointer',
          fontFamily: 'Manrope, sans-serif',
          fontSize: 11,
          fontWeight: 700,
          color: '#151133',
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 18,
            height: 18,
            padding: '0 5px',
            background: '#F9550D',
            color: 'white',
            borderRadius: 9,
            fontSize: 10,
            fontWeight: 700,
          }}
        >
          {missing}
        </span>
        <span>{label}</span>
      </button>

      {hovered && (
        <span
          role="tooltip"
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 8px)',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#151133',
            color: 'white',
            padding: '8px 12px',
            borderRadius: 8,
            fontFamily: 'Manrope, sans-serif',
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1.5,
            whiteSpace: 'nowrap',
            boxShadow: '0 6px 18px rgba(21, 17, 51, 0.22)',
            zIndex: 50,
            pointerEvents: 'none',
          }}
        >
          <span style={{ display: 'block', fontWeight: 700, fontSize: 11, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
            Missing
          </span>
          {fields.map((f, i) => (
            <span key={i} style={{ display: 'block' }}>
              · {f}
            </span>
          ))}
          {/* Down-pointing arrow */}
          <span
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '6px solid #151133',
            }}
          />
        </span>
      )}
    </span>
  );
};

export default SectionCompletenessPill;
